import React, { useEffect, useMemo, useState } from 'react';
import {
  useQueryClient,
} from 'react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Input,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { ExpandMore } from '@material-ui/icons';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { useHistory } from 'react-router-dom';
import PageTitle from '../../ui/PageTitle';
import { useButtonsStyles } from '../../../styles/useButtonsStyles';
import { useListingsFiltrationStyles } from '../../../styles/useListingsFiltrationStyles';
import Modal from '../../modals/Modal/Modal';
import LoaderModal from '../../modals/LoaderModal/LoaderModal';
import {
  useYardiSyncPropertiesQuery,
  useYardiSyncRunsQuery,
  useYardiSyncSessionQuery,
  useYardiSyncTaskStatusQuery,
} from '../../../queries/yardiSync';
import {
  useTriggerYardiSyncRunMutation,
  useUpdateYardiSyncSessionMutation,
  useUpsertYardiSyncPropertyMutation,
} from '../../../queries/yardiSync/mutations';
import {
  YardiSyncProperty,
  YardiSyncRun,
  YardiSyncRunResponse,
} from '../../../types/yardiSync';
import { formatDateTimeToLocal } from '../../../formatters/dateFormatters/dateFormatters';
import { PRINT_HYPHEN } from '../../../constants';

const RUNS_LIMIT = 50;
const YARDI_PROPERTY_SYNC_TYPE = 'property_sync';
const YARDI_LEASE_DOCUMENT_SYNC_TYPE = 'lease_documents';

const usePanelStyles = makeStyles(() => ({
  panel: {
    padding: '16px',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  statusPill: {
    borderRadius: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: 600,
    padding: '4px 10px',
    textTransform: 'capitalize',
  },
}));

const isTerminalStatus = (status?: string): boolean => {
  if (!status) {
    return false;
  }
  return ['completed', 'partial', 'failed'].includes(status);
};

const statusStyles = (status?: string) => {
  switch (status) {
    case 'completed':
      return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
    case 'partial':
      return { backgroundColor: '#fff3e0', color: '#ef6c00' };
    case 'failed':
      return { backgroundColor: '#ffebee', color: '#c62828' };
    case 'running':
      return { backgroundColor: '#e3f2fd', color: '#1565c0' };
    case 'queued':
      return { backgroundColor: '#f3e5f5', color: '#6a1b9a' };
    default:
      return { backgroundColor: '#eeeeee', color: '#424242' };
  }
};

const formatStatusLabel = (status?: string): string => {
  if (!status) {
    return 'unknown';
  }
  return status.replace(/_/g, ' ');
};

const formatSyncType = (syncType?: string | null): string => (
  syncType ? syncType.replace(/_/g, ' ') : PRINT_HYPHEN
);

const getErrorMessage = (error: unknown, fallback: string): string => {
  const responseData = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof responseData === 'string') {
    return responseData;
  }
  if (responseData && typeof responseData === 'object') {
    const data = responseData as { error?: string; message?: string };
    if (data.error) {
      return data.error;
    }
    if (data.message) {
      return data.message;
    }
  }
  const message = (error as Error)?.message;
  return message || fallback;
};

const YardiSyncDashboard: React.FC = () => {
  const buttonClasses = useButtonsStyles();
  const filterClasses = useListingsFiltrationStyles();
  const panelClasses = usePanelStyles();
  const history = useHistory();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [openPropertyModal, setOpenPropertyModal] = useState(false);
  const [openRunModal, setOpenRunModal] = useState(false);
  const [propertyForm, setPropertyForm] = useState({
    propertyCode: '',
    propertyId: '',
    propertyName: '',
    streetAddress: '',
    isActive: true,
  });
  const [propertyIdError, setPropertyIdError] = useState('');
  const [editingProperty, setEditingProperty] = useState<YardiSyncProperty | null>(null);
  const [selectedRunSyncType, setSelectedRunSyncType] = useState<string>(YARDI_PROPERTY_SYNC_TYPE);
  const [selectedRunPropertyCodes, setSelectedRunPropertyCodes] = useState<string[]>([]);
  const [runAsync, setRunAsync] = useState(true);
  const [runsOffset, setRunsOffset] = useState(0);
  const [runsList, setRunsList] = useState<YardiSyncRun[]>([]);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const {
    data: sessionData,
    isError: sessionError,
    error: sessionErrorData,
  } = useYardiSyncSessionQuery();
  const {
    data: propertiesData,
    isLoading: propertiesLoading,
    isError: propertiesError,
    error: propertiesErrorData,
  } = useYardiSyncPropertiesQuery();
  const {
    data: runsData,
    isLoading: runsLoading,
    isFetching: runsFetching,
    isError: runsError,
    error: runsErrorData,
  } = useYardiSyncRunsQuery(RUNS_LIMIT, runsOffset);

  const updateSessionMutation = useUpdateYardiSyncSessionMutation();
  const upsertPropertyMutation = useUpsertYardiSyncPropertyMutation();
  const triggerRunMutation = useTriggerYardiSyncRunMutation();

  const { data: taskStatus } = useYardiSyncTaskStatusQuery(activeTaskId, {
    refetchInterval: (data) => {
      if (!data || !data.status) {
        return 5000;
      }
      return isTerminalStatus(data.status) ? false : 5000;
    },
  });

  useEffect(() => {
    if (!runsData) {
      return;
    }
    setRunsList((prev) => (runsOffset === 0 ? runsData : [...prev, ...runsData]));
  }, [runsData, runsOffset]);

  useEffect(() => {
    if (!taskStatus || !taskStatus.status) {
      return;
    }
    if (isTerminalStatus(taskStatus.status)) {
      setRunsOffset(0);
      queryClient.invalidateQueries('yardi-sync-session');
      queryClient.invalidateQueries('yardi-sync-runs');
      queryClient.invalidateQueries('documentsList');
      queryClient.invalidateQueries('building-documents');
    }
  }, [queryClient, taskStatus]);

  const resetPropertyForm = () => {
    setPropertyForm({
      propertyCode: '',
      propertyId: '',
      propertyName: '',
      streetAddress: '',
      isActive: true,
    });
    setEditingProperty(null);
    setPropertyIdError('');
  };

  const handleOpenPropertyModal = (property?: YardiSyncProperty) => {
    if (property) {
      setPropertyForm({
        propertyCode: property.propertyCode,
        propertyId: property.propertyId,
        propertyName: property.propertyName,
        streetAddress: property.streetAddress || '',
        isActive: property.isActive,
      });
      setEditingProperty(property);
      setPropertyIdError('');
    } else {
      resetPropertyForm();
    }
    setOpenPropertyModal(true);
  };

  const handleSessionRefresh = async () => {
    try {
      await updateSessionMutation.mutateAsync({});
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'Unable to refresh session.'));
      setErrorModalOpen(true);
    }
  };

  const isPropertyIdNumeric = (value: string): boolean => /^\d+$/.test(value);

  const handlePropertySave = async () => {
    const propertyCode = propertyForm.propertyCode.trim();
    const propertyId = propertyForm.propertyId.trim();
    const propertyName = propertyForm.propertyName.trim();
    const streetAddress = propertyForm.streetAddress.trim();

    if (!propertyCode || !propertyId) {
      setErrorMessage('Property code and Property ID are required.');
      setErrorModalOpen(true);
      return;
    }
    if (!isPropertyIdNumeric(propertyId)) {
      setPropertyIdError('Property ID must be numeric.');
      return;
    }
    try {
      await upsertPropertyMutation.mutateAsync({
        propertyCode,
        propertyId,
        propertyName,
        streetAddress,
        isActive: propertyForm.isActive,
      });
      resetPropertyForm();
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'Unable to save property.'));
      setErrorModalOpen(true);
    }
  };

  const handleRunSubmit = async () => {
    try {
      const response: YardiSyncRunResponse = await triggerRunMutation.mutateAsync({
        syncType: selectedRunSyncType,
        propertyCodes: selectedRunPropertyCodes.length > 0
          ? selectedRunPropertyCodes
          : undefined,
        async: runAsync,
      });
      setOpenRunModal(false);
      setSelectedRunSyncType(YARDI_PROPERTY_SYNC_TYPE);
      setSelectedRunPropertyCodes([]);
      if (runAsync && response.taskId) {
        setActiveTaskId(response.taskId);
      } else if (response.runId) {
        setRunsOffset(0);
        queryClient.invalidateQueries('yardi-sync-session');
        queryClient.invalidateQueries('yardi-sync-runs');
        queryClient.invalidateQueries('documentsList');
        queryClient.invalidateQueries('building-documents');
      }
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'Unable to trigger run.'));
      setErrorModalOpen(true);
    }
  };

  const sessionStatus = !sessionData?.active
    ? 'missing'
    : sessionData?.lastValidationStatus || 'invalid';

  const sessionStatusLabel = sessionStatus === 'missing' ? 'Missing' : formatStatusLabel(sessionStatus);

  const renderStatusPill = (status?: string, label?: string) => (
    <Box component="span" className={panelClasses.statusPill} style={statusStyles(status)}>
      {label || formatStatusLabel(status)}
    </Box>
  );

  const propertyColumns = useMemo<MRT_ColumnDef<YardiSyncProperty>[]>(() => [
    {
      header: 'Property Code',
      accessorKey: 'propertyCode',
      enableSorting: false,
    },
    {
      header: 'Property ID',
      accessorKey: 'propertyId',
      enableSorting: false,
    },
    {
      header: 'Property Name',
      accessorKey: 'propertyName',
      enableSorting: false,
    },
    {
      header: 'Street Address',
      accessorKey: 'streetAddress',
      enableSorting: false,
    },
    {
      header: 'Active',
      accessorKey: 'isActive',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => (
        <Checkbox checked={row.isActive} color="primary" disabled />
      ),
      enableSorting: false,
    },
    {
      header: 'Actions',
      accessorKey: 'actions',
      size: 100,
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => (
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenPropertyModal(row);
          }}
        >
          Edit
        </Button>
      ),
      enableSorting: false,
    },
  ], []);

  const runColumns = useMemo<MRT_ColumnDef<YardiSyncRun>[]>(() => [
    {
      header: 'Run ID',
      accessorKey: 'id',
      enableSorting: false,
      size: 80,
    },
    {
      header: 'Sync Type',
      accessorKey: 'syncType',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => formatSyncType(row.syncType),
      enableSorting: false,
      size: 140,
    },
    {
      header: 'Status',
      accessorKey: 'status',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => renderStatusPill(row.status),
      enableSorting: false,
      size: 120,
    },
    {
      header: 'Started',
      accessorKey: 'startedAt',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => (row.startedAt ? formatDateTimeToLocal(row.startedAt) : PRINT_HYPHEN),
      enableSorting: false,
      size: 160,
    },
    {
      header: 'Finished',
      accessorKey: 'finishedAt',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => (row.finishedAt ? formatDateTimeToLocal(row.finishedAt) : PRINT_HYPHEN),
      enableSorting: false,
      size: 160,
    },
    {
      header: 'Notes',
      accessorKey: 'notes',
      enableSorting: false,
      size: 200,
    },
    {
      header: 'Actions',
      accessorKey: 'actions',
      // eslint-disable-next-line react/display-name
      accessorFn: (row) => (
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            history.push(`/yardi/runs/${row.id}`);
          }}
        >
          View Details
        </Button>
      ),
      enableSorting: false,
      size: 140,
    },
  ], [history]);

  const hasMoreRuns = runsData ? runsData.length === RUNS_LIMIT : false;

  const sessionPanelContent = (
    <>
      {sessionError ? (
        <Typography color="error">
          {getErrorMessage(sessionErrorData, 'Unable to load session.')}
        </Typography>
      ) : (
        <>
          <Box>
            <Typography variant="subtitle2">Status</Typography>
            {renderStatusPill(sessionStatus, sessionStatusLabel)}
          </Box>
          <Box mt={2}>
            <Typography variant="body2">
              Session cookies are fetched automatically from Rubicon when needed. No manual cookie update is required.
            </Typography>
          </Box>
          <Box mt={2}>
            <Typography variant="body2">
              {`Last validated: ${sessionData?.lastValidatedAt
                ? formatDateTimeToLocal(sessionData.lastValidatedAt)
                : PRINT_HYPHEN}`}
            </Typography>
            <Typography variant="body2">
              {`Message: ${sessionData?.lastValidationMessage || PRINT_HYPHEN}`}
            </Typography>
          </Box>
        </>
      )}
    </>
  );

  const propertiesPanelContent = (
    propertiesError ? (
      <Typography color="error">
        {getErrorMessage(propertiesErrorData, 'Unable to load properties.')}
      </Typography>
    ) : (
      <MaterialReactTable
        columns={propertyColumns}
        data={propertiesData || []}
        enableTopToolbar={false}
        enableColumnResizing
        enableColumnActions={false}
        enableFullScreenToggle={false}
        enableRowOrdering={false}
        enableSorting={false}
        enableGlobalFilterModes={false}
        enableDensityToggle={false}
        state={{ isLoading: propertiesLoading }}
        layoutMode="grid"
        muiTableProps={{
          sx: {
            tableLayout: 'fixed',
            display: 'table',
          },
        }}
      />
    )
  );

  const runsPanelContent = (
    <>
      {taskStatus && (
        <Box mb={2}>
          <Typography variant="body2">
            {`Async task: ${taskStatus.taskId} `}
            {renderStatusPill(taskStatus.status)}
            {taskStatus.runId && ` (Run ${taskStatus.runId})`}
          </Typography>
        </Box>
      )}
      {runsError ? (
        <Typography color="error">
          {getErrorMessage(runsErrorData, 'Unable to load runs.')}
        </Typography>
      ) : (
        <>
          <MaterialReactTable
            columns={runColumns}
            data={runsList}
            enableTopToolbar={false}
            enableColumnResizing
            enableColumnActions={false}
            enableFullScreenToggle={false}
            enableRowOrdering={false}
            enableSorting={false}
            enableGlobalFilterModes={false}
            enableDensityToggle={false}
            state={{ isLoading: runsLoading || runsFetching }}
            layoutMode="grid"
            muiTableProps={{
              sx: {
                tableLayout: 'fixed',
                display: 'table',
              },
            }}
          />
          {hasMoreRuns && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Button
                className={buttonClasses.standard}
                variant="outlined"
                color="primary"
                disabled={runsFetching}
                onClick={() => setRunsOffset((prev) => prev + RUNS_LIMIT)}
              >
                Load more
              </Button>
            </Box>
          )}
        </>
      )}
    </>
  );

  const renderPanel = (title: string, action: JSX.Element | null, content: JSX.Element) => {
    if (isMobile) {
      return (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">{title}</Typography>
          </AccordionSummary>
          <AccordionDetails style={{ display: 'block' }}>
            {action && <Box mb={2}>{action}</Box>}
            {content}
          </AccordionDetails>
        </Accordion>
      );
    }
    return (
      <Paper variant="outlined" className={panelClasses.panel}>
        <Box className={panelClasses.panelHeader}>
          <Typography variant="h6">{title}</Typography>
          {action}
        </Box>
        {content}
      </Paper>
    );
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={3}>
        <PageTitle>Yardi Sync</PageTitle>
      </Box>
      <Box display="flex" flexDirection="column" style={{ gap: '24px' }}>
        {renderPanel(
          'Session',
          (
            <Button
              className={buttonClasses.standard}
              variant="outlined"
              color="primary"
              disabled={updateSessionMutation.isLoading}
              onClick={handleSessionRefresh}
            >
              Refresh Session
            </Button>
          ),
          sessionPanelContent,
        )}
        {renderPanel(
          'Properties',
          (
            <Button
              className={buttonClasses.standard}
              variant="contained"
              color="primary"
              onClick={() => handleOpenPropertyModal()}
            >
              Add Property
            </Button>
          ),
          propertiesPanelContent,
        )}
        {renderPanel(
          'Runs',
          (
            <Button
              className={buttonClasses.standard}
              variant="contained"
              color="primary"
              onClick={() => setOpenRunModal(true)}
            >
              Run Now
            </Button>
          ),
          runsPanelContent,
        )}
      </Box>
      <Modal
        isOpen={openPropertyModal}
        handleClose={() => setOpenPropertyModal(false)}
        title={editingProperty ? 'Edit Property' : 'Add Property'}
        confirmText={editingProperty ? 'Save' : 'Add'}
        confirmAction={handlePropertySave}
        cancelText="Cancel"
        cancelAction={() => setOpenPropertyModal(false)}
        body={(
          <Box
            className={filterClasses.listingFilterWrapper}
            display="flex"
            flexDirection="column"
            style={{ gap: '16px' }}
          >
            <TextField
              label="Property Code"
              value={propertyForm.propertyCode}
              onChange={(event) => setPropertyForm((prev) => ({
                ...prev,
                propertyCode: event.target.value,
              }))}
              disabled={!!editingProperty}
              variant="outlined"
            />
            <TextField
              label="Property ID"
              value={propertyForm.propertyId}
              onChange={(event) => {
                const { value } = event.target;
                const trimmedValue = value.trim();
                setPropertyForm((prev) => ({
                  ...prev,
                  propertyId: value,
                }));
                if (!trimmedValue || isPropertyIdNumeric(trimmedValue)) {
                  setPropertyIdError('');
                }
              }}
              error={!!propertyIdError}
              helperText={propertyIdError}
              variant="outlined"
            />
            <TextField
              label="Property Name"
              value={propertyForm.propertyName}
              onChange={(event) => setPropertyForm((prev) => ({
                ...prev,
                propertyName: event.target.value,
              }))}
              variant="outlined"
            />
            <TextField
              label="Street Address"
              value={propertyForm.streetAddress}
              onChange={(event) => setPropertyForm((prev) => ({
                ...prev,
                streetAddress: event.target.value,
              }))}
              variant="outlined"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={propertyForm.isActive}
                  onChange={(event) => setPropertyForm((prev) => ({
                    ...prev,
                    isActive: event.target.checked,
                  }))}
                  color="primary"
                />
              )}
              label="Active"
            />
          </Box>
        )}
      />
      <Modal
        isOpen={openRunModal}
        handleClose={() => setOpenRunModal(false)}
        title="Run Now"
        confirmText="Run"
        confirmAction={handleRunSubmit}
        cancelText="Cancel"
        cancelAction={() => setOpenRunModal(false)}
        body={(
          <Box
            className={filterClasses.listingFilterWrapper}
            display="flex"
            flexDirection="column"
            style={{ gap: '16px' }}
          >
            <FormControl>
              <InputLabel id="yardi-run-sync-type">Sync Type</InputLabel>
              <Select
                labelId="yardi-run-sync-type"
                value={selectedRunSyncType}
                onChange={(event) => {
                  setSelectedRunSyncType(event.target.value as string);
                  setSelectedRunPropertyCodes([]);
                }}
              >
                <MenuItem value={YARDI_PROPERTY_SYNC_TYPE}>Property Sync</MenuItem>
                <MenuItem value={YARDI_LEASE_DOCUMENT_SYNC_TYPE}>Lease Documents</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="textSecondary">
              {selectedRunSyncType === YARDI_LEASE_DOCUMENT_SYNC_TYPE
                ? 'Lease document sync updates local lease documents for the selected properties. '
                  + 'Leave Properties empty to run every property with Yardi-managed buildings.'
                : 'Property sync runs the standard Yardi import for the selected properties.'}
            </Typography>
            <FormControl>
              <InputLabel id="yardi-run-properties">Properties (optional)</InputLabel>
              <Select
                labelId="yardi-run-properties"
                multiple
                value={selectedRunPropertyCodes}
                onChange={(event) => setSelectedRunPropertyCodes(event.target.value as string[])}
                input={<Input />}
                renderValue={(selected) => (selected as string[]).join(', ')}
              >
                {(propertiesData || []).map((property) => (
                  <MenuItem key={property.propertyCode} value={property.propertyCode}>
                    <Checkbox checked={selectedRunPropertyCodes.indexOf(property.propertyCode) > -1} />
                    <Typography variant="body2">
                      {`${property.propertyCode} - ${property.propertyName || property.propertyId}`}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={runAsync}
                  onChange={(event) => setRunAsync(event.target.checked)}
                  color="primary"
                />
              )}
              label="Run asynchronously"
            />
          </Box>
        )}
      />
      <Modal
        isOpen={errorModalOpen}
        handleClose={() => setErrorModalOpen(false)}
        title="Error"
        body={errorMessage}
        confirmText="OK"
        confirmAction={() => setErrorModalOpen(false)}
      />
      <LoaderModal
        isOpen={
          updateSessionMutation.isLoading
          || upsertPropertyMutation.isLoading
          || triggerRunMutation.isLoading
        }
        msg="Processing..."
      />
    </>
  );
};

export default YardiSyncDashboard;
